/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import isEqual from 'lodash/isEqual';
import { Placeholder, useFlags } from 'sarsaparilla';
import SimpleMap from 'shared-ui/maps/templates/SimpleMap';
import { featureCollection } from 'shared-ui/maps/turfUtils';

import { makeGeoJsonData } from '../../utils';

mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN;

export function MapContainer({
    facilityData,
    tours,
    tourImageMap,
    isLoading,
    selectedTourCardId,
    selectedTourEvent,
}) {
    const tourPinsFlag = useFlags().tourPins;
    const [geojson, setGeojson] = useState(featureCollection([]));
    const [selectedFeatureId, setSelectedFeatureId] = useState(null);
    const [featureName, setFeatureName] = useState(facilityData?.facility_name);

    useState(() => {
        const updatedName = facilityData?.facility_name;
        if (updatedName && !isEqual(updatedName, featureName)) {
            setFeatureName(updatedName);
        }
    }, [facilityData.facility_name]);

    // Only include tours that have coord data
    const filteredTourPins = useMemo(() => {
        return tours.reduce((result, tour) => {
            if (
                tour.tour_latitude &&
                tour.tour_longitude &&
                !(tour.tour_latitude === 0 && tour.tour_longitude === 0)
            ) {
                result.push({
                    ...tour,
                    image_url:
                        tourImageMap &&
                        tourImageMap[`${tour?.facility_id}_${tour?.tour_id}`]
                            ? tourImageMap[`${tour?.facility_id}_${tour?.tour_id}`]
                                  ?.previewImage?.url
                            : '',
                });
            }
            return result;
        }, []);
    }, [tourImageMap, tours]);

    const mapCenter = useMemo(() => {
        let center = [facilityData?.facility_longitude, facilityData?.facility_latitude];
        if (tourPinsFlag && filteredTourPins?.length) {
            // center the map using first tour coordinates
            const firstTour = filteredTourPins[0];
            center = [firstTour?.tour_longitude, firstTour?.tour_latitude];
        }

        return center;
    }, [
        facilityData?.facility_latitude,
        facilityData?.facility_longitude,
        filteredTourPins,
        tourPinsFlag,
    ]);

    useEffect(() => {
        setSelectedFeatureId(selectedTourCardId);
    }, [selectedTourCardId]);

    // Hook to update map when tours are filtered by availability
    useEffect(() => {
        const facilityParams = {
            id: facilityData.facility_id,
            latitude: facilityData.facility_latitude,
            longitude: facilityData.facility_longitude,
        };
        const geoJsonData = makeGeoJsonData(
            facilityParams,
            filteredTourPins,
            tourPinsFlag
        );

        setGeojson(featureCollection(geoJsonData));
    }, [
        facilityData.facility_id,
        facilityData.facility_latitude,
        facilityData.facility_longitude,
        filteredTourPins,
        tourPinsFlag,
    ]);

    if (isLoading) {
        return (
            <div data-ti-map>
                <Placeholder height="100%" />
            </div>
        );
    }

    return (
        <div id="Mapbox" className="map">
            <SimpleMap
                featureName={featureName}
                geojson={geojson}
                config={{ jumpMapToPin: true }}
                mapContainer="Mapbox"
                center={mapCenter}
                zoom={12}
                service="timedentry"
                selectedFeatureId={selectedFeatureId}
                selectedEvent={selectedTourEvent}
            />
        </div>
    );
}

MapContainer.propTypes = {
    facilityData: PropTypes.object,
    tours: PropTypes.array,
    tourImageMap: PropTypes.object,
    isLoading: PropTypes.bool,
    selectedTourCardId: PropTypes.string,
    selectedTourEvent: PropTypes.func,
};

MapContainer.defaultProps = {
    tours: [],
};
